import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  EllipsisVertical,
  UserCheck,
  ShieldUser,
  KeyRound,
  AlertTriangle,
  User as UserIcon,
} from "lucide-react";
import useSWR from "swr";
import { cn } from "@/lib/utils";
import { useAuth } from "@/features/auth/context/auth-context";
import { useUserProfile } from "@/hooks/use-user-profile";
import { useUserCompanies } from "@/hooks/use-user-companies";
import { authApi } from "@/lib/api";
import {
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  useSidebar,
} from "@/components/ui/sidebar";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ThemeToggle } from "@/components/theme-toggle";
import { switchCompany } from "@/lib/switch-company";

export function NavUser() {
  const navigate = useNavigate();
  const { selectedCompanyId, isSystemMode, logout } = useAuth();
  const { profile: userProfile } = useUserProfile();
  const { companies } = useUserCompanies();
  const { data: accessSummary } = useSWR(
    selectedCompanyId ? ["/auth/access-summary", selectedCompanyId] : null,
    () => authApi.accessSummary(),
    { revalidateOnFocus: false, dedupingInterval: 30000 },
  );
  const { state } = useSidebar();

  const selectedCompany = useMemo(() => {
    if (!selectedCompanyId || !companies) return null;
    return companies.find((c) => c.id === selectedCompanyId) || null;
  }, [selectedCompanyId, companies]);

  const positions = useMemo(
    () => accessSummary?.positions ?? [],
    [accessSummary],
  );
  const roles = useMemo(() => accessSummary?.roles ?? [], [accessSummary]);

  const initials = userProfile
    ? `${userProfile.name.charAt(0)}${userProfile.lastName.charAt(0)}`.toUpperCase()
    : "??";

  const handleCompanyChange = (companyId: string) => {
    switchCompany(companyId);
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const isCollapsed = state === "collapsed";

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className={cn(
                "data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground",
                !isCollapsed && "px-2",
              )}
            >
              <Avatar className="h-8 w-8 rounded-lg">
                <AvatarFallback className="rounded-lg bg-sidebar-accent text-xs font-medium text-sidebar-accent-foreground">
                  {initials}
                </AvatarFallback>
              </Avatar>
              {!isCollapsed && (
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">
                    {userProfile?.name} {userProfile?.lastName}
                  </span>
                  <span className="truncate text-xs text-sidebar-foreground/70">
                    {isSystemMode
                      ? "Plataforma"
                      : (selectedCompany?.name ?? "Sin empresa")}

                  </span>
                </div>
              )}
              {!isCollapsed && (
                <EllipsisVertical className="ml-auto h-4 w-4 text-sidebar-foreground/70" />
              )}
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            side={isCollapsed ? "right" : "top"}
            align={isCollapsed ? "center" : "end"}
            className="w-72 min-w-0 rounded-xl"
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                <Avatar className="h-8 w-8 rounded-lg">
                  <AvatarFallback className="rounded-lg">
                    {initials}
                  </AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">
                    {userProfile?.name} {userProfile?.lastName}
                  </span>
                  <span className="truncate text-xs text-muted-foreground">
                    {isSystemMode
                      ? "Plataforma"
                      : (selectedCompany?.name ?? "Sin empresa")}
                  </span>
                </div>
              </div>
            </DropdownMenuLabel>

            <DropdownMenuSeparator />

            {/* Company selector - oculto en modo SYSTEM */}
            {!isSystemMode && companies && companies.length > 1 && (
              <>
                <DropdownMenuGroup>
                  <DropdownMenuLabel className="text-xs text-muted-foreground">
                    Sociedades
                  </DropdownMenuLabel>
                  {companies
                    .filter((c) => c.active !== false)
                    .map((company) => (
                      <DropdownMenuItem
                        key={company.id}
                        onClick={() => handleCompanyChange(company.id)}
                        className={cn(
                          company.id === selectedCompanyId &&
                            "bg-accent font-medium",
                        )}
                      >
                        <UserIcon className="h-4 w-4" />
                        {company.name}
                      </DropdownMenuItem>
                    ))}
                </DropdownMenuGroup>
                <DropdownMenuSeparator />
              </>
            )}

            {/* Access summary */}
            <div className="p-2 space-y-2">
              <p className="text-xs font-medium text-muted-foreground">
                Resumen de acceso
              </p>
              <div className="grid grid-cols-3 gap-1">
                <div className="rounded-md border bg-muted/40 px-2 py-1.5 text-center">
                  <div className="flex items-center justify-center gap-1 text-muted-foreground">
                    <UserCheck className="h-3 w-3" />
                    <span className="text-[10px]">Puestos de trabajo</span>
                  </div>
                  <p className="text-xs font-semibold">{positions.length}</p>
                </div>
                <div className="rounded-md border bg-muted/40 px-2 py-1.5 text-center">
                  <div className="flex items-center justify-center gap-1 text-muted-foreground">
                    <ShieldUser className="h-3 w-3" />
                    <span className="text-[10px]">Roles</span>
                  </div>
                  <p className="text-xs font-semibold">{roles.length}</p>
                </div>
                <div className="rounded-md border bg-muted/40 px-2 py-1.5 text-center">
                  <div className="flex items-center justify-center gap-1 text-muted-foreground">
                    <KeyRound className="h-3 w-3" />
                    <span className="text-[10px]">Permisos</span>
                  </div>
                  <p className="text-xs font-semibold">
                    {accessSummary?.permissions?.length ?? 0}
                  </p>
                </div>
              </div>
              {positions.length === 0 && (
                <div className="rounded-md border border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950 px-2 py-1.5 text-xs text-red-700 dark:text-red-400">
                  <div className="flex items-center gap-1.5">
                    <AlertTriangle className="h-3 w-3" />
                    <span>No tienes puesto de trabajo activo.</span>
                  </div>
                </div>
              )}
            </div>

            <DropdownMenuSeparator />

            <DropdownMenuGroup>
              <DropdownMenuItem
                onClick={() => navigate("/profile")}
                className="cursor-pointer"
              >
                <UserIcon className="h-4 w-4" />
                Mi perfil
              </DropdownMenuItem>
            </DropdownMenuGroup>

            <DropdownMenuSeparator />

            <div className="flex items-center justify-between px-2 py-1">
              <span className="text-xs text-muted-foreground">Tema</span>
              <ThemeToggle />
            </div>

            <DropdownMenuSeparator />

            <DropdownMenuItem
              onClick={handleLogout}
              className="cursor-pointer text-destructive focus:text-destructive"
            >
              Cerrar sesión
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
